import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ClipboardList,
  ArrowLeft,
  User,
  Users,
  Building2,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Send,
  FileText,
  Upload,
  Download,
  Trash2,
  RefreshCw,
  MessageSquare,
  ArrowRightLeft,
  Calendar,
  Edit,
  Save,
  Plus,
  Loader2,
  UserPlus,
  ChevronRight,
  History,
  CheckSquare,
  Square,
  Link2,
  Briefcase,
  X,
  Search
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';

const TramiteDetalle = () => {
  const { tramiteId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [tramite, setTramite] = useState(null);
  const [users, setUsers] = useState([]);
  const [availableUsersForDelegation, setAvailableUsersForDelegation] = useState([]);
  const [config, setConfig] = useState(null);
  
  // Estados de UI
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Comentario
  const [nuevoComentario, setNuevoComentario] = useState('');
  const [enviandoComentario, setEnviandoComentario] = useState(false);
  
  // Delegación
  const [showDelegarDialog, setShowDelegarDialog] = useState(false);
  const [delegarData, setDelegarData] = useState({ nuevo_responsable_id: '', motivo: '', mantener_como_participante: true });
  const [delegando, setDelegando] = useState(false);
  
  // Documentos
  const [uploadingDoc, setUploadingDoc] = useState(false);
  
  // Agregar participante
  const [showAddParticipante, setShowAddParticipante] = useState(false);
  const [nuevoParticipanteId, setNuevoParticipanteId] = useState('');
  
  // Subtareas
  const [nuevaSubtarea, setNuevaSubtarea] = useState('');
  const [creandoSubtarea, setCreandoSubtarea] = useState(false);
  
  // Timeline
  const [timeline, setTimeline] = useState([]);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  
  // Vinculación
  const [showVincularCaso, setShowVincularCaso] = useState(false);
  const [showVincularCliente, setShowVincularCliente] = useState(false);
  const [casosDisponibles, setCasosDisponibles] = useState([]);
  const [clientesDisponibles, setClientesDisponibles] = useState([]);
  const [buscandoCasos, setBuscandoCasos] = useState(false);
  const [buscandoClientes, setBuscandoClientes] = useState(false);
  const [casoSeleccionado, setCasoSeleccionado] = useState('');
  const [clienteSeleccionado, setClienteSeleccionado] = useState('');
  
  // Reasignación de grupo
  const [showReasignarGrupo, setShowReasignarGrupo] = useState(false);
  const [grupos, setGrupos] = useState([]);
  const [nuevoGrupoId, setNuevoGrupoId] = useState('');
  const [motivoReasignacion, setMotivoReasignacion] = useState('');
  const [reasignando, setReasignando] = useState(false);
  useEffect(() => {
    loadData();
  }, [tramiteId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [tramiteRes, usersRes, configRes, delegationUsersRes, gruposRes] = await Promise.all([
        api.get(`/tramites/${tramiteId}`),
        api.get('/users'),
        api.get('/tramites/config'),
        api.get('/groups/derivation/available-users').catch(() => ({ data: { usuarios_disponibles: [] } })),
        api.get('/groups').catch(() => ({ data: [] }))
      ]);
      setTramite(tramiteRes.data);
      setUsers(usersRes.data);
      setConfig(configRes.data);
      setGrupos(gruposRes.data || []);
      // Filtrar usuarios disponibles para delegación (excluir usuario actual)
      setAvailableUsersForDelegation(
        delegationUsersRes.data?.usuarios_disponibles?.filter(u => !u.es_mismo_usuario) || []
      );
      setEditData(tramiteRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar el trámite');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/tramites/${tramiteId}`, {
        titulo: editData.titulo,
        descripcion: editData.descripcion,
        prioridad: editData.prioridad,
        fecha_vencimiento: editData.fecha_vencimiento
      });
      toast.success('Trámite actualizado');
      setEditMode(false);
      loadData();
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleCambiarEstado = async (nuevoEstado) => {
    try {
      await api.post(`/tramites/${tramiteId}/estado`, { estado: nuevoEstado });
      toast.success(`Estado cambiado a ${nuevoEstado}`);
      loadData();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleDelegar = async () => {
    if (!delegarData.nuevo_responsable_id) {
      toast.error('Seleccione el nuevo responsable');
      return;
    }
    setDelegando(true);
    try {
      const res = await api.post(`/tramites/${tramiteId}/delegar`, delegarData);
      toast.success(res.data?.email_enviado 
        ? 'Trámite delegado. Se envió notificación por email.' 
        : 'Trámite delegado correctamente');
      setShowDelegarDialog(false);
      setDelegarData({ nuevo_responsable_id: '', motivo: '', mantener_como_participante: true });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al delegar');
    } finally {
      setDelegando(false);
    }
  };

  const handleReasignarGrupo = async () => {
    if (!nuevoGrupoId) {
      toast.error('Seleccione el nuevo grupo');
      return;
    }
    setReasignando(true);
    try {
      await api.post('/permisos-v2/reasignar-grupo', {
        registro_id: tramiteId,
        entidad: 'tramite',
        nuevo_grupo_id: nuevoGrupoId,
        motivo: motivoReasignacion
      });
      toast.success('Trámite reasignado al nuevo grupo. Se notificó a los supervisores.');
      setShowReasignarGrupo(false);
      setNuevoGrupoId('');
      setMotivoReasignacion('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reasignar');
    } finally {
      setReasignando(false);
    }
  };

  const handleEnviarComentario = async () => {
    if (!nuevoComentario.trim()) return;
    setEnviandoComentario(true);
    try {
      await api.post(`/tramites/${tramiteId}/comentarios`, { contenido: nuevoComentario, menciones: [] });
      toast.success('Comentario agregado');
      setNuevoComentario('');
      loadData();
    } catch (error) {
      toast.error('Error al enviar comentario');
    } finally {
      setEnviandoComentario(false);
    }
  };

  const handleUploadDoc = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingDoc(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('descripcion', '');
    
    try {
      await api.post(`/tramites/${tramiteId}/adjuntos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Documento subido');
      loadData();
    } catch (error) {
      toast.error('Error al subir documento');
    } finally {
      setUploadingDoc(false);
    }
  };

  // === SUBTAREAS ===
  const handleCrearSubtarea = async () => {
    if (!nuevaSubtarea.trim()) return;
    setCreandoSubtarea(true);
    try {
      await api.post(`/tramites/${tramiteId}/subtareas`, { 
        titulo: nuevaSubtarea,
        orden: (tramite.subtareas?.length || 0) + 1
      });
      toast.success('Subtarea creada');
      setNuevaSubtarea('');
      loadData();
    } catch (error) {
      toast.error('Error al crear subtarea');
    } finally {
      setCreandoSubtarea(false);
    }
  };

  const handleToggleSubtarea = async (subtareaId, completada) => {
    try {
      await api.put(`/tramites/${tramiteId}/subtareas/${subtareaId}`, { completada: !completada });
      loadData();
    } catch (error) {
      toast.error('Error al actualizar subtarea');
    }
  };

  const handleEliminarSubtarea = async (subtareaId) => {
    try {
      await api.delete(`/tramites/${tramiteId}/subtareas/${subtareaId}`);
      toast.success('Subtarea eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar subtarea');
    }
  };

  // === TIMELINE ===
  const loadTimeline = async () => {
    setLoadingTimeline(true);
    try {
      const res = await api.get(`/tramites/${tramiteId}/timeline`);
      setTimeline(res.data);
    } catch (error) {
      console.error('Error loading timeline:', error);
    } finally {
      setLoadingTimeline(false);
    }
  };

  // === VINCULACIÓN CASOS ===
  const buscarCasos = async (termino) => {
    if (!termino || termino.length < 2) {
      setCasosDisponibles([]);
      return;
    }
    setBuscandoCasos(true);
    try {
      const res = await api.get(`/cases?search=${termino}&limit=10`);
      // Filtrar casos ya vinculados
      const vinculados = tramite.casos_vinculados?.map(c => c.caso_id) || [];
      setCasosDisponibles(res.data.filter(c => !vinculados.includes(c.id)));
    } catch (error) {
      console.error('Error buscando casos:', error);
    } finally {
      setBuscandoCasos(false);
    }
  };

  const handleVincularCaso = async () => {
    if (!casoSeleccionado) return;
    try {
      await api.post(`/tramites/${tramiteId}/vincular-caso`, { caso_id: casoSeleccionado });
      toast.success('Caso vinculado');
      setShowVincularCaso(false);
      setCasoSeleccionado('');
      loadData();
    } catch (error) {
      toast.error('Error al vincular caso');
    }
  };

  const handleDesvincularCaso = async (casoId) => {
    try {
      await api.delete(`/tramites/${tramiteId}/vincular-caso/${casoId}`);
      toast.success('Caso desvinculado');
      loadData();
    } catch (error) {
      toast.error('Error al desvincular caso');
    }
  };

  // === VINCULACIÓN CLIENTES ===
  const buscarClientes = async (termino) => {
    if (!termino || termino.length < 2) {
      setClientesDisponibles([]);
      return;
    }
    setBuscandoClientes(true);
    try {
      const res = await api.get(`/clientes?search=${termino}&limit=10`);
      // Filtrar clientes ya vinculados
      const vinculados = tramite.clientes_vinculados?.map(c => c.cliente_id) || [];
      setClientesDisponibles(res.data.filter(c => !vinculados.includes(c.id)));
    } catch (error) {
      console.error('Error buscando clientes:', error);
    } finally {
      setBuscandoClientes(false);
    }
  };

  const handleVincularCliente = async () => {
    if (!clienteSeleccionado) return;
    try {
      await api.post(`/tramites/${tramiteId}/vincular-cliente`, { cliente_id: clienteSeleccionado });
      toast.success('Cliente vinculado');
      setShowVincularCliente(false);
      setClienteSeleccionado('');
      loadData();
    } catch (error) {
      toast.error('Error al vincular cliente');
    }
  };

  const handleDesvincularCliente = async (clienteId) => {
    try {
      await api.delete(`/tramites/${tramiteId}/vincular-cliente/${clienteId}`);
      toast.success('Cliente desvinculado');
      loadData();
    } catch (error) {
      toast.error('Error al desvincular cliente');
    }
  };

  const handleDeleteDoc = async (docId) => {
    try {
      await api.delete(`/tramites/${tramiteId}/documentos/${docId}`);
      toast.success('Documento eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar documento');
    }
  };

  const handleAddParticipante = async () => {
    if (!nuevoParticipanteId) return;
    try {
      await api.post(`/tramites/${tramiteId}/participantes/${nuevoParticipanteId}`);
      toast.success('Participante agregado');
      setShowAddParticipante(false);
      setNuevoParticipanteId('');
      loadData();
    } catch (error) {
      toast.error('Error al agregar participante');
    }
  };

  const handleRemoveParticipante = async (userId) => {
    try {
      await api.delete(`/tramites/${tramiteId}/participantes/${userId}`);
      toast.success('Participante removido');
      loadData();
    } catch (error) {
      toast.error('Error al remover participante');
    }
  };

  const getEstadoConfig = (estado) => {
    switch (estado) {
      case 'NUEVO': return { color: 'bg-purple-100 text-purple-700', icon: Clock };
      case 'EN_PROCESO': return { color: 'bg-blue-100 text-blue-700', icon: RefreshCw };
      case 'EN_REVISION': return { color: 'bg-amber-100 text-amber-700', icon: AlertTriangle };
      case 'COMPLETADO': return { color: 'bg-green-100 text-green-700', icon: CheckCircle };
      case 'CANCELADO': return { color: 'bg-slate-100 text-slate-500', icon: XCircle };
      default: return { color: 'bg-slate-100 text-slate-600', icon: Clock };
    }
  };

  const getPrioridadColor = (prioridad) => {
    switch (prioridad) {
      case 'URGENTE': return 'bg-red-100 text-red-700';
      case 'ALTA': return 'bg-amber-100 text-amber-700';
      case 'MEDIA': return 'bg-blue-100 text-blue-700';
      case 'BAJA': return 'bg-slate-100 text-slate-600';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('es-ES', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch { return dateStr; }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-indigo-600" size={32} />
      </div>
    );
  }

  if (!tramite) {
    return (
      <div className="p-6 text-center text-slate-500">
        Trámite no encontrado
      </div>
    );
  }

  const EstadoIcon = getEstadoConfig(tramite.estado).icon;

  return (
    <div className="p-6 space-y-6" data-testid="tramite-detalle-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft size={18} />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <Badge className={getEstadoConfig(tramite.estado).color}>
              <EstadoIcon size={14} className="mr-1" />
              {tramite.estado?.replace('_', ' ')}
            </Badge>
            <Badge className={getPrioridadColor(tramite.prioridad)}>
              {tramite.prioridad}
            </Badge>
            {tramite.dias_para_vencer !== undefined && (
              <Badge className={tramite.dias_para_vencer < 0 ? 'bg-red-100 text-red-700' : tramite.dias_para_vencer <= 3 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}>
                {tramite.dias_para_vencer < 0 
                  ? `Vencido hace ${Math.abs(tramite.dias_para_vencer)} días`
                  : tramite.dias_para_vencer === 0 
                    ? 'Vence hoy'
                    : `${tramite.dias_para_vencer} días restantes`}
              </Badge>
            )}
          </div>
          {editMode ? (
            <Input
              className="mt-2 text-xl font-bold"
              value={editData.titulo}
              onChange={(e) => setEditData({ ...editData, titulo: e.target.value })}
            />
          ) : (
            <h1 className="text-xl font-bold text-slate-900 mt-1">{tramite.titulo}</h1>
          )}
        </div>
        <div className="flex gap-2">
          {editMode ? (
            <>
              <Button variant="outline" onClick={() => setEditMode(false)}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
                Guardar
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setEditMode(true)}>
                <Edit size={16} className="mr-2" />
                Editar
              </Button>
              <Button variant="outline" onClick={() => setShowDelegarDialog(true)} className="text-indigo-600 border-indigo-300">
                <ArrowRightLeft size={16} className="mr-2" />
                Delegar
              </Button>
              {grupos.length > 0 && (
                <Button variant="outline" onClick={() => setShowReasignarGrupo(true)} className="text-purple-600 border-purple-300">
                  <Users size={16} className="mr-2" />
                  Cambiar Grupo
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Columna Principal */}
        <div className="lg:col-span-2 space-y-6">
          {/* Descripción */}
          <Card className="p-5 bg-white">
            <h3 className="font-medium text-slate-900 mb-3">Descripción</h3>
            {editMode ? (
              <Textarea
                value={editData.descripcion || ''}
                onChange={(e) => setEditData({ ...editData, descripcion: e.target.value })}
                rows={4}
              />
            ) : (
              <p className="text-slate-600 whitespace-pre-wrap">
                {tramite.descripcion || 'Sin descripción'}
              </p>
            )}
          </Card>

          {/* Timeline de Delegaciones - VISUAL */}
          {tramite.historial_delegaciones?.length > 0 && (
            <Card className="p-5 bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
              <h3 className="font-medium text-indigo-900 mb-4 flex items-center gap-2">
                <ArrowRightLeft size={18} />
                Historial de Delegaciones
              </h3>
              <div className="relative">
                {/* Línea conectora */}
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-indigo-200"></div>
                
                <div className="space-y-4">
                  {tramite.historial_delegaciones.map((d, idx) => (
                    <div key={d.id || idx} className="relative flex items-start gap-4 pl-10">
                      {/* Punto en la línea */}
                      <div className="absolute left-2 top-2 w-4 h-4 rounded-full bg-indigo-500 border-2 border-white shadow"></div>
                      
                      <div className="flex-1 bg-white rounded-lg p-3 shadow-sm border border-indigo-100">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex items-center">
                            <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                              <User size={14} className="text-slate-600" />
                            </div>
                            <span className="ml-2 font-medium text-slate-700">
                              {d.de_usuario?.nombre || 'Usuario'}
                            </span>
                          </div>
                          <ChevronRight size={16} className="text-indigo-400" />
                          <div className="flex items-center">
                            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                              <User size={14} className="text-indigo-600" />
                            </div>
                            <span className="ml-2 font-medium text-indigo-700">
                              {d.a_usuario?.nombre || 'Usuario'}
                            </span>
                          </div>
                        </div>
                        {d.motivo && (
                          <p className="text-sm text-slate-600 italic">"{d.motivo}"</p>
                        )}
                        <p className="text-xs text-slate-400 mt-1">
                          {formatDateTime(d.fecha)} • Delegado por {d.delegado_por_nombre}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          )}

          {/* Comentarios / Timeline */}
          <Card className="p-5 bg-white">
            <h3 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
              <MessageSquare size={18} />
              Comentarios y Actividad
            </h3>
            
            {/* Input de comentario */}
            <div className="flex gap-2 mb-4">
              <Textarea
                placeholder="Escribir comentario..."
                value={nuevoComentario}
                onChange={(e) => setNuevoComentario(e.target.value)}
                rows={2}
                className="flex-1"
              />
              <Button 
                onClick={handleEnviarComentario} 
                disabled={enviandoComentario || !nuevoComentario.trim()}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                {enviandoComentario ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
              </Button>
            </div>
            
            {/* Lista de comentarios */}
            <div className="space-y-3">
              {tramite.comentarios?.length === 0 ? (
                <p className="text-slate-500 text-center py-4">Sin comentarios aún</p>
              ) : (
                tramite.comentarios?.slice().reverse().map((c, idx) => (
                  <div key={c.id || idx} className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <User size={16} className="text-indigo-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{c.usuario?.nombre || c.usuario_nombre}</span>
                        <span className="text-xs text-slate-400">{formatDateTime(c.fecha)}</span>
                      </div>
                      <p className="text-slate-600 mt-1">{c.contenido}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>

          {/* === SUBTAREAS === */}
          <Card className="p-5 bg-white">
            <h3 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
              <CheckSquare size={18} />
              Subtareas ({tramite.subtareas?.filter(s => s.completada).length || 0}/{tramite.subtareas?.length || 0})
            </h3>
            
            {/* Agregar subtarea */}
            <div className="flex gap-2 mb-4">
              <Input
                placeholder="Nueva subtarea..."
                value={nuevaSubtarea}
                onChange={(e) => setNuevaSubtarea(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleCrearSubtarea()}
                className="flex-1"
              />
              <Button 
                onClick={handleCrearSubtarea} 
                disabled={creandoSubtarea || !nuevaSubtarea.trim()}
                size="sm"
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                {creandoSubtarea ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />}
              </Button>
            </div>
            
            {/* Lista de subtareas */}
            {tramite.subtareas?.length === 0 ? (
              <p className="text-slate-500 text-center py-4 text-sm">Sin subtareas. Divida el trámite en pasos más pequeños.</p>
            ) : (
              <div className="space-y-2">
                {tramite.subtareas?.map((s) => (
                  <div 
                    key={s.id} 
                    className={`flex items-center gap-3 p-2 rounded-lg ${s.completada ? 'bg-green-50' : 'bg-slate-50'} group`}
                  >
                    <button
                      onClick={() => handleToggleSubtarea(s.id, s.completada)}
                      className="flex-shrink-0"
                    >
                      {s.completada ? (
                        <CheckSquare size={18} className="text-green-600" />
                      ) : (
                        <Square size={18} className="text-slate-400 hover:text-indigo-600" />
                      )}
                    </button>
                    <span className={`flex-1 text-sm ${s.completada ? 'text-green-700 line-through' : 'text-slate-700'}`}>
                      {s.titulo}
                    </span>
                    {s.completada && s.completed_at && (
                      <span className="text-xs text-green-600">
                        {formatDateTime(s.completed_at)}
                      </span>
                    )}
                    <button
                      onClick={() => handleEliminarSubtarea(s.id)}
                      className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 p-1"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Barra de progreso */}
            {tramite.subtareas?.length > 0 && (
              <div className="mt-4">
                <div className="flex justify-between text-xs text-slate-500 mb-1">
                  <span>Progreso</span>
                  <span>{Math.round((tramite.subtareas?.filter(s => s.completada).length / tramite.subtareas?.length) * 100)}%</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full transition-all"
                    style={{ width: `${(tramite.subtareas?.filter(s => s.completada).length / tramite.subtareas?.length) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </Card>

          {/* === VINCULACIONES === */}
          <Card className="p-5 bg-white">
            <h3 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
              <Link2 size={18} />
              Vinculaciones
            </h3>
            
            {/* Casos vinculados */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">Casos ({tramite.casos_vinculados?.length || 0})</span>
                <Button variant="outline" size="sm" onClick={() => setShowVincularCaso(true)}>
                  <Plus size={12} className="mr-1" /> Vincular
                </Button>
              </div>
              {tramite.casos_vinculados?.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-2">Sin casos vinculados</p>
              ) : (
                <div className="space-y-1">
                  {tramite.casos_vinculados?.map((c) => (
                    <div key={c.caso_id} className="flex items-center justify-between p-2 bg-blue-50 rounded text-sm">
                      <div className="flex items-center gap-2">
                        <Briefcase size={14} className="text-blue-600" />
                        <span className="text-blue-800 truncate max-w-[180px]">{c.caso_titulo}</span>
                      </div>
                      <button 
                        onClick={() => handleDesvincularCaso(c.caso_id)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Clientes vinculados */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">Clientes ({tramite.clientes_vinculados?.length || 0})</span>
                <Button variant="outline" size="sm" onClick={() => setShowVincularCliente(true)}>
                  <Plus size={12} className="mr-1" /> Vincular
                </Button>
              </div>
              {tramite.clientes_vinculados?.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-2">Sin clientes vinculados</p>
              ) : (
                <div className="space-y-1">
                  {tramite.clientes_vinculados?.map((c) => (
                    <div key={c.cliente_id} className="flex items-center justify-between p-2 bg-purple-50 rounded text-sm">
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-purple-600" />
                        <div>
                          <span className="text-purple-800 truncate block max-w-[150px]">{c.cliente_nombre}</span>
                          {c.cliente_cuit && <span className="text-xs text-purple-500">{c.cliente_cuit}</span>}
                        </div>
                      </div>
                      <button 
                        onClick={() => handleDesvincularCliente(c.cliente_id)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>

          {/* === TIMELINE === */}
          <Card className="p-5 bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-slate-900 flex items-center gap-2">
                <History size={18} />
                Timeline de Actividad
              </h3>
              <Button variant="ghost" size="sm" onClick={loadTimeline}>
                <RefreshCw size={14} className={loadingTimeline ? 'animate-spin' : ''} />
              </Button>
            </div>
            
            {timeline.length === 0 && !loadingTimeline ? (
              <p className="text-slate-500 text-center py-4 text-sm">
                <button onClick={loadTimeline} className="text-indigo-600 hover:underline">
                  Cargar timeline
                </button>
              </p>
            ) : loadingTimeline ? (
              <div className="flex justify-center py-4">
                <Loader2 className="animate-spin text-indigo-600" size={20} />
              </div>
            ) : (
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {timeline.map((item, idx) => (
                  <div key={item.id || idx} className="flex gap-3 text-sm">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      item.tipo === 'COMENTARIO' ? 'bg-blue-100' :
                      item.tipo === 'ESTADO_CAMBIADO' ? 'bg-amber-100' :
                      item.tipo === 'SUBTAREA_COMPLETADA' ? 'bg-green-100' :
                      item.tipo === 'CASO_VINCULADO' || item.tipo === 'CLIENTE_VINCULADO' ? 'bg-purple-100' :
                      'bg-slate-100'
                    }`}>
                      {item.tipo === 'COMENTARIO' ? <MessageSquare size={14} className="text-blue-600" /> :
                       item.tipo === 'ESTADO_CAMBIADO' ? <RefreshCw size={14} className="text-amber-600" /> :
                       item.tipo === 'SUBTAREA_COMPLETADA' || item.tipo === 'SUBTAREA_CREADA' ? <CheckSquare size={14} className="text-green-600" /> :
                       item.tipo === 'CASO_VINCULADO' || item.tipo === 'CLIENTE_VINCULADO' ? <Link2 size={14} className="text-purple-600" /> :
                       <Clock size={14} className="text-slate-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-slate-700">{item.descripcion}</p>
                      <p className="text-xs text-slate-400">
                        {item.usuario_nombre} • {formatDateTime(item.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Documentos */}
          <Card className="p-5 bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-slate-900 flex items-center gap-2">
                <FileText size={18} />
                Documentos ({tramite.documentos?.length || 0})
              </h3>
              <label className="cursor-pointer">
                <input type="file" className="hidden" onChange={handleUploadDoc} disabled={uploadingDoc} />
                <Button variant="outline" size="sm" disabled={uploadingDoc} asChild>
                  <span>
                    {uploadingDoc ? <Loader2 className="animate-spin mr-2" size={14} /> : <Upload size={14} className="mr-2" />}
                    Subir
                  </span>
                </Button>
              </label>
            </div>
            
            {tramite.documentos?.length === 0 ? (
              <p className="text-slate-500 text-center py-4">Sin documentos</p>
            ) : (
              <div className="space-y-2">
                {tramite.documentos?.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText size={18} className="text-slate-400" />
                      <div>
                        <p className="font-medium text-slate-900">{doc.filename}</p>
                        <p className="text-xs text-slate-500">
                          {(doc.size / 1024).toFixed(1)} KB • Subido por {doc.subido_por_nombre} • {formatDateTime(doc.fecha)}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/tramites/${tramiteId}/documentos/${doc.id}/download`, '_blank')}
                      >
                        <Download size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteDoc(doc.id)}>
                        <Trash2 size={14} className="text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Acciones rápidas */}
          <Card className="p-4 bg-white">
            <h3 className="font-medium text-slate-900 mb-3">Cambiar Estado</h3>
            <div className="grid grid-cols-2 gap-2">
              {['NUEVO', 'EN_PROCESO', 'EN_REVISION', 'COMPLETADO'].map(estado => {
                const estadoConfig = getEstadoConfig(estado);
                const EstIcon = estadoConfig.icon;
                return (
                  <Button
                    key={estado}
                    variant={tramite.estado === estado ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleCambiarEstado(estado)}
                    disabled={tramite.estado === estado}
                    className={tramite.estado === estado ? estadoConfig.color : ''}
                  >
                    <EstIcon size={14} className="mr-1" />
                    {estado.replace('_', ' ')}
                  </Button>
                );
              })}
            </div>
          </Card>

          {/* Participantes - VISUAL */}
          <Card className="p-4 bg-white">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-slate-900 flex items-center gap-2">
                <Users size={18} />
                Equipo Asignado
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setShowAddParticipante(true)}>
                <UserPlus size={14} />
              </Button>
            </div>
            
            {/* Responsable Principal */}
            <div className="mb-4">
              <Label className="text-xs text-slate-500">Responsable Principal</Label>
              <div className="flex items-center gap-3 mt-1 p-2 bg-indigo-50 rounded-lg border border-indigo-200">
                <div className="w-10 h-10 rounded-full bg-indigo-500 flex items-center justify-center text-white font-medium">
                  {tramite.responsable?.nombre?.charAt(0) || '?'}
                </div>
                <div>
                  <p className="font-medium text-indigo-900">{tramite.responsable?.nombre || 'Sin asignar'}</p>
                  <p className="text-xs text-indigo-600">{tramite.responsable?.email}</p>
                </div>
              </div>
            </div>
            
            {/* Asignados Adicionales */}
            {tramite.asignados?.length > 0 && (
              <div className="mb-4">
                <Label className="text-xs text-slate-500">Asignados Adicionales</Label>
                <div className="space-y-2 mt-1">
                  {tramite.asignados.map((a, idx) => (
                    <div key={a.id || idx} className="flex items-center justify-between p-2 bg-blue-50 rounded-lg border border-blue-100">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium text-sm">
                          {a.nombre?.charAt(0) || '?'}
                        </div>
                        <div>
                          <span className="text-sm font-medium text-blue-900">{a.nombre}</span>
                          {a.email && <p className="text-xs text-blue-600">{a.email}</p>}
                        </div>
                      </div>
                      <Badge className="bg-blue-100 text-blue-700 text-xs">Asignado</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Otros participantes/observadores */}
            {tramite.participantes?.length > 0 && (
              <div>
                <Label className="text-xs text-slate-500">Participantes (Observadores)</Label>
                <div className="space-y-2 mt-1">
                  {tramite.participantes.map((p, idx) => (
                    <div key={p.id || idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-medium text-sm">
                          {p.nombre?.charAt(0) || '?'}
                        </div>
                        <span className="text-sm">{p.nombre}</span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleRemoveParticipante(p.id)}>
                        <XCircle size={14} className="text-slate-400" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Avatares stack visual */}
            <div className="flex items-center mt-4 pt-3 border-t">
              <div className="flex -space-x-2">
                <div className="w-8 h-8 rounded-full bg-indigo-500 border-2 border-white flex items-center justify-center text-white text-xs font-medium z-20" title={tramite.responsable?.nombre}>
                  {tramite.responsable?.nombre?.charAt(0) || '?'}
                </div>
                {tramite.asignados?.slice(0, 3).map((a, idx) => (
                  <div 
                    key={a.id || idx}
                    className="w-8 h-8 rounded-full bg-blue-500 border-2 border-white flex items-center justify-center text-white text-xs font-medium"
                    style={{ zIndex: 15 - idx }}
                    title={a.nombre}
                  >
                    {a.nombre?.charAt(0) || '?'}
                  </div>
                ))}
                {tramite.participantes?.slice(0, 4 - (tramite.asignados?.length || 0)).map((p, idx) => (
                  <div 
                    key={p.id || idx}
                    className="w-8 h-8 rounded-full bg-slate-300 border-2 border-white flex items-center justify-center text-slate-600 text-xs font-medium"
                    style={{ zIndex: 5 - idx }}
                    title={p.nombre}
                  >
                    {p.nombre?.charAt(0) || '?'}
                  </div>
                ))}
                {((tramite.asignados?.length || 0) + (tramite.participantes?.length || 0)) > 4 && (
                  <div className="w-8 h-8 rounded-full bg-slate-200 border-2 border-white flex items-center justify-center text-slate-500 text-xs">
                    +{(tramite.asignados?.length || 0) + (tramite.participantes?.length || 0) - 4}
                  </div>
                )}
              </div>
              <span className="ml-3 text-sm text-slate-500">
                {1 + (tramite.participantes?.length || 0)} persona(s)
              </span>
            </div>
          </Card>

          {/* Detalles */}
          <Card className="p-4 bg-white">
            <h3 className="font-medium text-slate-900 mb-3">Detalles</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Origen</span>
                <Badge style={{ backgroundColor: (config?.origenes?.find(o => o.id === tramite.origen)?.color || '#6b7280') + '20', color: config?.origenes?.find(o => o.id === tramite.origen)?.color || '#6b7280' }}>
                  {config?.origenes?.find(o => o.id === tramite.origen)?.nombre || tramite.origen}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Tipo</span>
                <span className="text-slate-900">{config?.tipos?.find(t => t.id === tramite.tipo)?.nombre || tramite.tipo}</span>
              </div>
              {tramite.referencia_externa && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Referencia</span>
                  <span className="text-slate-900 font-mono">{tramite.referencia_externa}</span>
                </div>
              )}
              {tramite.empresa && (
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">Empresa</span>
                  <span className="text-slate-900 flex items-center gap-1">
                    <Building2 size={12} />
                    {tramite.empresa.razon_social}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-slate-500">Vencimiento</span>
                <span className={tramite.dias_para_vencer < 0 ? 'text-red-600 font-medium' : 'text-slate-900'}>
                  {new Date(tramite.fecha_vencimiento).toLocaleDateString('es-ES')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Creado</span>
                <span className="text-slate-900">{formatDateTime(tramite.created_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Última actividad</span>
                <span className="text-slate-900">{formatDateTime(tramite.ultima_actividad)}</span>
              </div>
            </div>
          </Card>

          {/* Historial de Estados */}
          {tramite.historial_estados?.length > 0 && (
            <Card className="p-4 bg-white">
              <h3 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                <History size={16} />
                Historial de Estados
              </h3>
              <div className="space-y-2">
                {tramite.historial_estados.slice().reverse().slice(0, 5).map((h, idx) => {
                  const hConfig = getEstadoConfig(h.estado);
                  const HIcon = hConfig.icon;
                  return (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <HIcon size={14} className={hConfig.color.replace('bg-', 'text-').split(' ')[1]} />
                      <span className="text-slate-700">{h.estado?.replace('_', ' ')}</span>
                      <span className="text-slate-400 text-xs ml-auto">
                        {formatDateTime(h.fecha)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Dialog Delegar */}
      <Dialog open={showDelegarDialog} onOpenChange={setShowDelegarDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="text-indigo-600" size={20} />
              Delegar Trámite
            </DialogTitle>
            <DialogDescription>
              Transfiera la responsabilidad de este trámite a otro usuario. Se enviará notificación por email.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Nuevo Responsable *</Label>
              <Select value={delegarData.nuevo_responsable_id} onValueChange={(v) => setDelegarData({ ...delegarData, nuevo_responsable_id: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar usuario..." />
                </SelectTrigger>
                <SelectContent>
                  {availableUsersForDelegation.filter(u => u.id !== tramite?.responsable_id).map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      <div className="flex items-center gap-2">
                        <span>{u.nombre}</span>
                        <span className="text-xs text-slate-500">({u.rol})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Solo puede delegar a usuarios de su mismo nivel o inferior
              </p>
            </div>
            
            <div>
              <Label>Motivo de la delegación</Label>
              <Textarea
                value={delegarData.motivo}
                onChange={(e) => setDelegarData({ ...delegarData, motivo: e.target.value })}
                placeholder="Ej: Vacaciones, reasignación de tareas..."
              />
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={delegarData.mantener_como_participante}
                onChange={(e) => setDelegarData({ ...delegarData, mantener_como_participante: e.target.checked })}
                className="rounded border-slate-300"
              />
              <Label className="font-normal">Mantener al responsable actual como participante</Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDelegarDialog(false)}>Cancelar</Button>
            <Button onClick={handleDelegar} disabled={delegando} className="bg-indigo-600 hover:bg-indigo-700 text-white">
              {delegando ? <Loader2 className="animate-spin mr-2" size={16} /> : <ArrowRightLeft size={16} className="mr-2" />}
              Delegar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Agregar Participante */}
      <Dialog open={showAddParticipante} onOpenChange={setShowAddParticipante}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Agregar Participante</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Select value={nuevoParticipanteId} onValueChange={setNuevoParticipanteId}>
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar usuario..." />
              </SelectTrigger>
              <SelectContent>
                {users.filter(u => u.id !== tramite.responsable_id && !tramite.participantes_ids?.includes(u.id)).map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddParticipante(false)}>Cancelar</Button>
            <Button onClick={handleAddParticipante} className="bg-indigo-600 hover:bg-indigo-700 text-white">
              <UserPlus size={16} className="mr-2" />
              Agregar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Reasignar Grupo */}
      <Dialog open={showReasignarGrupo} onOpenChange={setShowReasignarGrupo}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="text-purple-600" size={20} />
              Reasignar a Otro Grupo
            </DialogTitle>
            <DialogDescription>
              Cambie el grupo al que pertenece este trámite. Se notificará a los supervisores de ambos grupos.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Grupo actual */}
            {tramite.grupo_id && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <Label className="text-xs text-slate-500">Grupo actual</Label>
                <p className="font-medium text-slate-900">
                  {grupos.find(g => g.id === tramite.grupo_id)?.nombre || 'Sin grupo'}
                </p>
              </div>
            )}
            
            {/* Nuevo grupo */}
            <div>
              <Label className="font-medium">Nuevo grupo *</Label>
              <Select value={nuevoGrupoId} onValueChange={setNuevoGrupoId}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccionar grupo..." />
                </SelectTrigger>
                <SelectContent>
                  {grupos.filter(g => g.id !== tramite.grupo_id && g.activo !== false).map(g => (
                    <SelectItem key={g.id} value={g.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: g.color || '#3b82f6' }} />
                        {g.nombre}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Motivo */}
            <div>
              <Label className="font-medium">Motivo (opcional)</Label>
              <Textarea
                className="mt-1"
                value={motivoReasignacion}
                onChange={(e) => setMotivoReasignacion(e.target.value)}
                placeholder="Razón de la reasignación..."
                rows={2}
              />
            </div>
            
            <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-800">
              <strong>Nota:</strong> Al reasignar, el nuevo grupo tomará control total del trámite según sus permisos configurados.
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReasignarGrupo(false)}>Cancelar</Button>
            <Button 
              onClick={handleReasignarGrupo} 
              disabled={reasignando || !nuevoGrupoId} 
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {reasignando ? <Loader2 className="animate-spin mr-2" size={16} /> : <Users size={16} className="mr-2" />}
              Reasignar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TramiteDetalle;
